// Klang: Womit klingt eine MIDI-Spur? Skizzen-Instrument im Browser oder eure eigenen Plugins
// (Nexus, Tonehub, Sample-Instrumente) über die Bridge. Gerendert wird auf dem Studio-Rechner –
// Rohsounds und Samples verlassen ihn nie, hochgeladen wird nur das fertige Audio.
import { useState } from 'react';
import { midiEvents } from '../audio/segments.ts';
import { useStudio } from '../state/store.ts';
import { bridge } from '../bridge/client.ts';
import { useBridge } from './Dante.tsx';
import { Panel, toast } from '../components/ui.tsx';
import { PRESETS } from '../audio/instruments.ts';

export function Klang() {
  const project = useStudio((s) => s.project);
  const commit = useStudio((s) => s.commit);
  const b = useBridge();
  const [busy, setBusy] = useState<string | null>(null);
  const instruments = b.plugins.filter((p) => p.isInstrument);
  const midiTracks = project.tracks.filter((t) => t.kind === 'midi');

  const render = async (trackId: string) => {
    const t = project.tracks.find((x) => x.id === trackId)!;
    const clips = project.clips.filter((c) => c.trackId === trackId && c.notes?.length);
    if (!t.instrument?.plugin.startsWith('bridge:')) return toast('Zuerst ein Plugin aus der Bridge wählen');
    if (!clips.length) return toast('Keine Noten auf der Spur');
    setBusy(trackId);
    try {
      // Mit Tempo-Spur: Zeiten in Sekunden schicken und 60 BPM angeben (1 Schlag = 1 Sekunde), so stimmen Tempowechsel
      const notes = clips.flatMap((c) => midiEvents(project, c, 0, Infinity).map((e) => ({ t: e.at, d: e.dur, m: e.n.m, v: e.n.v })));
      const lengthBeats = Math.max(...clips.map((c) => c.start + c.duration));
      const jobId = crypto.randomUUID();
      bridge.send({ type: 'renderMidi', jobId, pluginId: t.instrument.plugin.slice(7), preset: t.instrument.preset, bpm: 60, lengthBeats, notes, sampleRate: project.sampleRate });
      const done = await bridge.waitFor('renderDone', (m) => m.jobId === jobId, 120000);
      toast(`Gerendert: ${(done.frames / done.sampleRate).toFixed(1)} s. Upload als Audio-Spur folgt in Phase 3.`);
    } catch (e) { toast((e as Error).message); }
    finally { setBusy(null); }
  };

  return (
    <div className="grid-side">
      <Panel title="Instrumente pro Spur">
        <div className="body">
          <table className="table">
            <thead><tr><th>Spur</th><th>Klingt mit</th><th>Preset</th><th /></tr></thead>
            <tbody>
              {midiTracks.map((t) => (
                <tr key={t.id}>
                  <td><span className="row"><i style={{ width: 10, height: 10, borderRadius: 3, background: t.color, display: 'inline-block' }} />{t.name}</span></td>
                  <td>
                    <select className="input" value={t.instrument?.plugin ?? 'srs:synth'} onChange={(e) => commit('Instrument', (p) => { const x = p.tracks.find((y) => y.id === t.id)!; x.instrument = { plugin: e.target.value, preset: e.target.value === 'srs:synth' ? 'keys' : undefined }; })}>
                      <optgroup label="Skizze im Browser"><option value="srs:synth">SRS Synth</option><option value="srs:drum">SRS Drums</option></optgroup>
                      {instruments.length > 0 && <optgroup label="Eure Plugins (Bridge)">{instruments.map((p) => <option key={p.id} value={`bridge:${p.id}`}>{p.vendor} {p.name} ({p.format})</option>)}</optgroup>}
                    </select>
                  </td>
                  <td>
                    {t.instrument?.plugin === 'srs:synth' ? (
                      <select className="input" value={t.instrument.preset ?? 'keys'} onChange={(e) => commit('Preset', (p) => { p.tracks.find((y) => y.id === t.id)!.instrument!.preset = e.target.value; })}>
                        {Object.keys(PRESETS).map((k) => <option key={k}>{k}</option>)}
                      </select>
                    ) : t.instrument?.plugin.startsWith('bridge:') ? (
                      <input className="input" placeholder="Preset-Name im Plugin" value={t.instrument.preset ?? ''} onChange={(e) => commit('Preset', (p) => { p.tracks.find((y) => y.id === t.id)!.instrument!.preset = e.target.value; }, { merge: true })} />
                    ) : <span className="muted">–</span>}
                  </td>
                  <td>{t.instrument?.plugin.startsWith('bridge:') && <button className="btn small" disabled={busy === t.id} onClick={() => void render(t.id)}>{busy === t.id ? 'Rendert …' : 'Mit Plugin rendern'}</button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
      <Panel title="Plugins auf dem Studio-Rechner" right={b.connected && <button className="btn small" onClick={() => bridge.send({ type: 'listPlugins', rescan: true })}>Neu scannen</button>}>
        <div className="body stack">
          {!b.connected ? <div className="t2">Bridge nicht verbunden. Unter <b>Dante</b> verbinden, dann erscheinen hier Nexus, Tonehub und eure Sample-Instrumente.</div>
            : b.plugins.length === 0 ? <div className="empty">Keine Plugins gefunden</div>
            : b.plugins.map((p) => <div key={p.id} className="row" style={{ justifyContent: 'space-between' }}><span>{p.vendor} <b>{p.name}</b></span><span className="pill">{p.format}{p.isInstrument ? ' · Instrument' : ''}</span></div>)}
          <div className="card wash t2" style={{ fontSize: 12 }}>Lizenzrechtlich sauber: Plugins und Samples laufen nur auf eurem Rechner. Auf den Server kommt nur das gerenderte Ergebnis.</div>
        </div>
      </Panel>
    </div>
  );
}
